<template>
  <section class="pb-1">
    <b-row
      v-if="isLoading"
      class="mb-50"
    >
      <b-col
        sm="3"
        class="d-flex"
      >
        <b-skeleton
          v-for="index in 3"
          :key="'skeleton'+index"
          class="mr-1"
          height="33px"
          width="30%"
        />
      </b-col>
      <b-col
        sm="9"
        class="d-flex justify-content-end"
      >
        <b-skeleton
          v-for="index in 3"
          :key="'b-skeleton' + index"
          class="mr-1"
          height="34px"
          :width="index === 1 ? '25%' : '7.5%'"
        />
      </b-col>
    </b-row>
    <b-row v-else>
      <b-col sm="3">
        <b-button
          v-for="(interval, index) in wrapper.chartIntervals"
          :key="'chartInterval-'+index"
          :class="wrapper.filter.chartInterval === interval.value ? 'text-primary' : 'text-secondary'"
          variant="link"
          class="mb-2 mr-1"
          size="sm"
          @click="setChartInterval(interval.name)"
        >
          {{ interval.name }}
        </b-button>
      </b-col>
      <b-col
        sm="9"
        class="d-flex justify-content-end"
      >
        <filter-wrapper
          :properties="selectedFilters"
          :width-offset="900"
          @clicked="openSelectedFilter"
          @cleared="clearFilters"
        />
        <b-dropdown
          ref="dateDropdown"
          menu-class="py-0 daterangepicker-menu"
          right
          class="mr-1"
          variant="outline-dark"
          size="sm"
          no-caret
        >
          <template #button-content>
            {{ dateRange }}
            <GjIcon
              name="Calendar"
              class="ml-50"
            />
          </template>
          <div class="p-2">
            <date-range-picker
              :ranges="predefinedRanges"
              default-range-select="defaultRange"
              :allow-compare="false"
              @submit="submitDateRange"
              @cancel="closeDropdown"
            />
          </div>
        </b-dropdown>
        <dropdown
          :title="$tk('ClientPortal.Admin.Dashboard.Filters')"
          class="mb-1 mr-1"
          :show-dropdown="showFilters"
          @change="toggleFilters"
        >
          <template #menu-items>
            <dropdown-submenu
              v-for="(filter, index) in wrapper.availableFilters"
              :key="filter.name + '-' + index"
              :input-id="filter.displayName"
              :text="filter.displayName"
              :selected-filter="selectedFilter(filter.displayName)"
              input-name="submenu"
              :checked="menu"
              @changeSelectedMenu="changeMenu"
            >
              <template #items>
                <dropdown-search-item
                  v-if="filter.availableOptions.length > 10"
                  :key="'search-'+filter.name"
                  :data="filter"
                />
                <dropdown-item
                  v-for="(options, optionIndex) in filter.availableOptions.filter(option => option.key.toLowerCase().startsWith(filter.search)).slice(0, 100)"
                  :key="options.value.optionCode + '-' + optionIndex"
                  :label="options.key"
                  :filter-name="filter.name"
                  :value="options.value.optionCode"
                  :menu-name="filter.displayName"
                  :selected="options.value.selected"
                  @change="includeOrExclude"
                />
              </template>
              <template #footer>
                <dropdown-clear-item
                  :title="$tk('ClientPortal.Filters.Clear')"
                  @click="clearFilters(filter.displayName, false)"
                />
              </template>
            </dropdown-submenu>
            <dropdown-clear-item
              :title="$tk('ClientPortal.Filters.ClearAll')"
              @click="clearFilters"
            />
          </template>
        </dropdown>
        <b-button
          variant="primary"
          class="mb-2"
          size="sm"
          @click="loadAllStatistics"
        >
          {{ $tk('ClientPortal.Admin.Dashboard.Apply') }}
        </b-button>
      </b-col>
    </b-row>
    <chart
      :chart-data="statsData.impressionsChartStats"
      :wrapper="wrapper"
      :is-loading="isLoading"
      :title="$tk('ClientPortal.Admin.Dashboard.Impressions')"
    >
      <template slot="header">
        <b-row>
          <StatisticsGraphInfo
            :data="statsData.summaryStats.impressions"
            :text="$tk('ClientPortal.Admin.Dashboard.Impressions')"
          />
          <StatisticsGraphInfo
            :data=" statsData.summaryStats.uniqueImpressions"
            :text="$tk('ClientPortal.Admin.Dashboard.UniqueImpressions')"
          />
          <StatisticsGraphInfo
            :data="statsData.summaryStats.impressionsProfit"
            :text="$tk('ClientPortal.Admin.Dashboard.ImpressionsProfit')"
            :symbol="$currencySymbol"
          />
        </b-row>
      </template>
    </chart>
    <chart
      :chart-data="statsData.viewsChartStats"
      :wrapper="wrapper"
      :is-loading="isLoading"
      :title="$tk('ClientPortal.Admin.Dashboard.Views')"
    >
      <template slot="header">
        <b-row>
          <StatisticsGraphInfo
            :data="statsData.summaryStats.views"
            :text="$tk('ClientPortal.Admin.Dashboard.Views')"
          />
          <StatisticsGraphInfo
            :data=" statsData.summaryStats.uniqueViews"
            :text="$tk('ClientPortal.Admin.Dashboard.UniqueViews')"
          />
          <StatisticsGraphInfo
            :data="statsData.summaryStats.viewsProfit"
            :text="$tk('ClientPortal.Admin.Dashboard.ViewsProfit')"
            :symbol="$currencySymbol"
          />
        </b-row>
      </template>
    </chart>
    <chart
      :chart-data="statsData.clicksChartStats"
      :wrapper="wrapper"
      :is-loading="isLoading"
      :title="$tk('ClientPortal.Admin.Dashboard.Clicks')"
    >
      <template slot="header">
        <b-row>
          <StatisticsGraphInfo
            :data="statsData.summaryStats.clicks"
            :text="$tk('ClientPortal.Admin.Dashboard.Clicks')"
          />
          <StatisticsGraphInfo
            :data=" statsData.summaryStats.uniqueClicks"
            :text="$tk('ClientPortal.Admin.Dashboard.UniqueClicks')"
          />
          <StatisticsGraphInfo
            :data="statsData.summaryStats.clicksProfit"
            :text="$tk('ClientPortal.Admin.Dashboard.ClicksProfit')"
            :symbol="$currencySymbol"
          />
        </b-row>
      </template>
    </chart>
    <chart
      :chart-data="statsData.actionsChartStats"
      :wrapper="wrapper"
      :is-loading="isLoading"
      :title="$tk('ClientPortal.Admin.Dashboard.Actions')"
    >
      <template slot="header">
        <b-row>
          <StatisticsGraphInfo
            :data="statsData.summaryStats.actions"
            :text="$tk('ClientPortal.Admin.Dashboard.Actions')"
          />
          <StatisticsGraphInfo
            :data=" statsData.summaryStats.uniqueActions"
            :text="$tk('ClientPortal.Admin.Dashboard.UniqueActions')"
          />
          <StatisticsGraphInfo
            :data="statsData.summaryStats.actionsProfit"
            :text="$tk('ClientPortal.Admin.Dashboard.ActionsProfit')"
            :symbol="$currencySymbol"
          />
        </b-row>
      </template>
    </chart>
    <chart
      :chart-data="statsData.fillRateWithProtectionChartStats"
      :wrapper="wrapper"
      :is-loading="isLoading"
      :title="$tk('ClientPortal.Admin.Dashboard.FillRate')"
      :subtitle="$tk('ClientPortal.Admin.Dashboard.IncludingAdProtections')"
    >
      <template slot="header">
        <b-row>
          <StatisticsGraphInfo
            :data="statsData.summaryStats.fillRateWithProtection"
            :text="$tk('ClientPortal.Admin.Dashboard.FillRate')"
            symbol="%"
          />
          <StatisticsGraphInfo
            :data=" statsData.summaryStats.requests"
            :text="$tk('ClientPortal.Admin.Dashboard.TotalRequests')"
          />
          <StatisticsGraphInfo
            :data="statsData.summaryStats.responses"
            :text="$tk('ClientPortal.Admin.Dashboard.TotalResponses')"
          />
        </b-row>
      </template>
    </chart>
    <chart
      :chart-data="statsData.fillRateWithoutProtectionChartStats"
      :wrapper="wrapper"
      :is-loading="isLoading"
      :title="$tk('ClientPortal.Admin.Dashboard.FillRate')"
      :subtitle="$tk('ClientPortal.Admin.Dashboard.ExcludingAdProtections')"
    >
      <template slot="header">
        <b-row>
          <StatisticsGraphInfo
            :data="statsData.summaryStats.fillRateWithoutProtection"
            :text="$tk('ClientPortal.Admin.Dashboard.FillRate')"
            symbol="%"
          />
          <StatisticsGraphInfo
            :data=" statsData.summaryStats.requests"
            :text="$tk('ClientPortal.Admin.Dashboard.TotalRequests')"
          />
          <StatisticsGraphInfo
            :data="statsData.summaryStats.responses"
            :text="$tk('ClientPortal.Admin.Dashboard.TotalResponses')"
          />
          <StatisticsGraphInfo
            :data="statsData.summaryStats.protections"
            :text="$tk('ClientPortal.Admin.Dashboard.TotalAdProtections')"
          />
        </b-row>
      </template>
    </chart>
    <chart
      :chart-data="statsData.adRequestVideoActionsChartStats"
      :wrapper="wrapper"
      :is-loading="isLoading"
      :title="$tk('ClientPortal.Admin.Dashboard.AdRequestToVideoImpressions')"
    />
    <b-card
      v-if="propertyStats.length > 0"
      class="mb-2"
    >
      <template #header>
        <h5 class="mb-1 mb-md-0 mr-0">
          {{ $tk('ClientPortal.Admin.Dashboard.Properties') }}
        </h5>
      </template>
      <Table
        :data="propertyTable"
        :columns="dashboardColumns"
        :is-loading="isLoading"
        type-of-table="dashboard"
        :enable-pagination="false"
        :enable-sort="false"
      />
    </b-card>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import {
  ref,
  computed,
  onMounted,
  reactive,
} from '@vue/composition-api'
import store from '@/store'
import DashboardService from '@/api/admin/dashboard.service'
import StatisticsGraph from '@/components/StatisticsGraph.vue'
import StatisticsGraphInfo from '@/components/StatisticsGraphInfo.vue'
import Table from '@/components/Table.vue'
import Dropdown from '@/components/Dropdown.vue'
import DropdownItem from '@/components/DropdownItem.vue'
import DropdownSubmenu from '@/components/DropdownSubmenu.vue'
import DropdownSearchItem from '@/components/DropdownSearchItem.vue'
import DropdownClearItem from '@/components/DropdownClearItem.vue'
import moment from 'moment'
import DateRangePicker from '@/components/VueDateRangePicker'
import translation from '@/mixins/translate'
import FilterWrapper from '@/components/FilterWrapper.vue'

export default {
  components: {
    GjIcon,
    chart: StatisticsGraph,
    StatisticsGraphInfo,
    Table,
    Dropdown,
    DropdownItem,
    DropdownSubmenu,
    DropdownSearchItem,
    DateRangePicker,
    DropdownClearItem,
    FilterWrapper,
  },
  filters: {
    formatValue(amount) {
      const amt = Number(amount)
      // eslint-disable-next-line no-mixed-operators
      return amt && amt.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '0'
    },
  },
  mounted() {
    this.$root.$on('bv::dropdown::show', () => {
      this.toggleFilters(false)
    })
  },
  setup(_, context) {
    const dashboardColumns = [
      {
        label: translation.methods.$tk('ClientPortal.Admin.Dashboard.Property'),
        field: 'property',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Dashboard.Impressions'),
        field: 'impressions',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Dashboard.UniqueImpressions'),
        field: 'uniqueImpressions',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Dashboard.Views'),
        field: 'views',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Dashboard.UniqueViews'),
        field: 'uniqueViews',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Dashboard.Clicks'),
        field: 'clicks',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Dashboard.UniqueClicks'),
        field: 'uniqueClicks',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Dashboard.Actions'),
        field: 'actions',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Dashboard.UniqueActions'),
        field: 'uniqueActions',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Dashboard.CTR'),
        field: 'ctr',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Dashboard.TotalRequests'),
        field: 'requests',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Dashboard.TotalResponses'),
        field: 'responses',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Dashboard.FillRate'),
        field: 'fillRate',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Dashboard.ImpressionsProfit'),
        field: 'impressionsProfit',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Dashboard.ViewsProfit'),
        field: 'viewsProfit',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Dashboard.ClicksProfit'),
        field: 'clicksProfit',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Dashboard.ActionsProfit'),
        field: 'actionsProfit',
      },
    ]
    const predefinedRanges = {
      today: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.Today'),
        startDate: moment(),
        endDate: moment(),
      },
      yesterday: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.Yesterday'),
        startDate: moment().subtract(1, 'days'),
        endDate: moment().subtract(1, 'days'),
      },
      last7Days: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.Last7Days'),
        startDate: moment().subtract(7, 'days'),
        endDate: moment().subtract(1, 'days'),
      },
      last30Days: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.Last30Days'),
        startDate: moment().subtract(30, 'days'),
        endDate: moment().subtract(1, 'days'),
      },
      lastMonth: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.LastMonth'),
        startDate: moment().subtract(1, 'months').startOf('month'),
        endDate: moment().subtract(1, 'month').endOf('month'),
      },
      defaultRange: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.DefaultRange'),
        startDate: moment().subtract(7, 'days'),
        endDate: moment(),
      },
    }
    const isLoading = ref(false)
    const menu = ref('')
    const showFilters = ref(false)
    const wrapper = computed(() => store.getters.dashboardModel)
    const propertyStats = computed(() => store.getters.propertyStats)
    const statsData = computed(() => store.getters.statsData)
    const propertyTable = computed(() => store.getters.propertyTable)
    const selectedFilters = reactive([])
    const dateRange = ref('')

    onMounted(async () => {
      isLoading.value = true
      const response = await DashboardService.getDashboard()
      store.commit('SET_DASHBOARD_DATA', response)
      await store.dispatch('getPropertyStats', response.filter)
      await store.dispatch('getStats', response.filter)
      dateRange.value = response.filter.dateRange
      isLoading.value = false
    })

    const toggleFilters = (toogleFilter = null) => {
      menu.value = ''
      showFilters.value = toogleFilter ?? !showFilters.value
    }

    const loadAllStatistics = async () => {
      isLoading.value = true
      await store.dispatch('getPropertyStats', wrapper.value.filter)
      await store.dispatch('getStats', wrapper.value.filter)
      isLoading.value = false
    }

    const setChartInterval = interval => {
      if (wrapper.value.filter.chartInterval === interval) { return }

      wrapper.value.filter.chartInterval = interval
    }

    const closeDropdown = () => {
      context.refs.dateDropdown.hide(true)
    }

    const prependZero = value => {
      if (value.toString().length === 1) {
        return `0${value}`
      }
      return value
    }

    const formatDate = value => {
      const day = prependZero(value.getDate())
      const month = prependZero(value.getMonth() + 1)
      const year = prependZero(value.getFullYear())

      return `${day}/${month}/${year}`
    }

    const getDateFromMomentRange = (startMoment, endMoment) => {
      const startDate = new Date(startMoment)
      const endDate = new Date(endMoment)
      return `${formatDate(startDate)} - ${formatDate(endDate)}`
    }

    const submitDateRange = range => {
      const { startDate, endDate } = range
      dateRange.value = getDateFromMomentRange(startDate, endDate)
      if (wrapper.value.filter.dateRange !== dateRange.value) { wrapper.value.filter.dateRange = dateRange.value }
      closeDropdown()
    }

    const changeMenu = newOpenedMenu => {
      menu.value === newOpenedMenu ? menu.value = '' : menu.value = newOpenedMenu
    }

    const updateSelectedFilters = (filter, count) => {
      const filterName = wrapper.value.availableFilters.find(f => f.name === filter)
      const filterObj = selectedFilters.find(f => f.name === filterName.displayName)
      if (filterObj) {
        if (count > 0) { filterObj.selectedFilters = count } else {
          const filterIndex = selectedFilters.findIndex(f => f.name === filterName.displayName)
          selectedFilters.splice(filterIndex, 1)
        }
      } else {
        selectedFilters.push({ name: filterName.displayName, selectedFilters: count })
      }
    }

    const includeOrExclude = (filter, option) => {
      const selectedOption = wrapper.value.availableFilters.find(f => f.name === filter).availableOptions.find(o => o.value.optionCode === option)
      const filters = wrapper.value.filter.includedFilters
      if (filter in filters) {
        const index = filters[filter].indexOf(option)
        if (index < 0) {
          filters[filter].push(option)
          selectedOption.value.selected = true
        } else {
          filters[filter].splice(index, 1)
          selectedOption.value.selected = false
        }
      } else {
        filters[filter] = []
        filters[filter].push(option)
        selectedOption.value.selected = true
      }
      updateSelectedFilters(filter, filters[filter].length)
    }

    const clearFilters = (filter = null, closeAllDropdowns = true) => {
      if (!filter) {
        selectedFilters.splice(0, selectedFilters.length)
        wrapper.value.filter.includedFilters = {}
        wrapper.value.availableFilters.forEach(f => {
          const selectedFilter = f.availableOptions.filter(option => option.value.selected === true)
          selectedFilter.forEach(selectedOption => {
            selectedOption.value.selected = false
          })
        })
        toggleFilters(false)
      } else {
        const filterIndex = selectedFilters.findIndex(f => f.name === filter)
        selectedFilters.splice(filterIndex, 1)
        const filterSelected = wrapper.value.availableFilters.find(f => f.displayName === filter)
        filterSelected.availableOptions.filter(option => option.value.selected === true).forEach(o => { o.value.selected = false })
        delete wrapper.value.filter.includedFilters[filter]
        if (closeAllDropdowns) { toggleFilters(false) }
      }
      menu.value = ''
    }

    const selectedFilter = name => {
      const filter = selectedFilters.findIndex(f => f.name === name)
      if (filter > -1) return true
      return false
    }

    const openSelectedFilter = filterName => {
      toggleFilters(true)
      menu.value = filterName
    }

    window.onclick = e => {
      if (!e.path.find(x => x.className?.includes('dropdown'))) {
        toggleFilters(false)
        menu.value = ''
      }
    }

    return {
      wrapper,
      propertyStats,
      statsData,
      isLoading,
      propertyTable,
      menu,
      changeMenu,
      includeOrExclude,
      selectedFilters,
      loadAllStatistics,
      setChartInterval,
      dateRange,
      predefinedRanges,
      submitDateRange,
      closeDropdown,
      clearFilters,
      toggleFilters,
      showFilters,
      dashboardColumns,
      selectedFilter,
      openSelectedFilter,
    }
  },
}
</script>

<style>

</style>
